import { call, all, put, takeLatest, select } from 'redux-saga/effects';
import AsyncStorage from '@react-native-community/async-storage';

import notification from '../../../services/notification';
import configurationService from '../../../services/modules/configuration/configuration-service';
import productService from '../product/product-service';
import { formatMessage } from './../../../i18n/translate';
import { GET_REQUEST, RELOAD_REQUEST, SAVE_REQUEST } from './configuration-constants';
import { CUSTOMIZABLE_CONTROL_CLIENT_REGISTER, ISA_CONFIGURATION, ISA_ERROR, ISA_TOKEN } from '../../../constants';
import * as configurationActions from './configuration-actions';

import reasonService from '../reason/reason-service';
import orderService from '../order/order-service';
import clientService from '../client/client-service';
import { ConfigurationInterface, UserInterface, TenantInterface } from '@e3labs/isa-data-dictionary';
import tenantService from '../tenant/tenant-service';
import { AppStateType } from '../root-reducer';

export function* getConfiguration() {
  yield put(configurationActions.setLoadingData(true));
  try {
    const localConfiguration = yield call(AsyncStorage.getItem, ISA_CONFIGURATION);
    const configuration = localConfiguration ? { ...JSON.parse(localConfiguration) } : null;
    if (configuration) {
      yield put(configurationActions.getSuccess(configuration));
    } else {
      const token = yield call(AsyncStorage.getItem, ISA_TOKEN);
      if (token) {
        try {
          const [{ data }, { data: reasons }, { data: clientActionCustomizableFields }] = yield all([
            yield call(configurationService.getConfiguration),
            yield call(reasonService.getAll),
            yield call(clientService.getCustomizableClientFields)
          ]);

          let deliveryControlSelected = null;

          if (data.defaultDeliveryControl) {
            deliveryControlSelected = (yield call(orderService.getDeliveries, {
              externalCodes: data.defaultDeliveryControl
            }))?.data?.[0];
          }

          let pix = null;

          if (configuration?.allowPaymentPix) {
            const { data: pixResponse } = yield call(configurationService.getPix);

            if (pixResponse) {
              pix = pixResponse;
            }
          }

          const clientField = CUSTOMIZABLE_CONTROL_CLIENT_REGISTER.filter(field =>
            clientActionCustomizableFields?.requiredFields?.some(
              clientActionField => field?.key === clientActionField?.name || clientActionField?.name === field?.name
            )
          );

          const blockReasonSelected = reasons.find(x => x.externalCode === data?.blockReason);
          if (data) {
            const payload = {
              ...data,
              reasons,
              blockReasonSelected,
              deliveryControlSelected,
              clientActionCustomizableFields: data?.allowClientActionFieldsCustomization ? clientField : null,
              pix
            };
            yield call(AsyncStorage.setItem, ISA_CONFIGURATION, JSON.stringify(payload));
            yield put(configurationActions.getSuccess(payload));
          }
        } catch (error) {
          yield put({ type: ISA_ERROR, error });
        }
      }
    }
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(configurationActions.setLoadingData(false));
  }
}

export function* saveConfiguration({
  payload,
  files,
  callbackUpload
}: {
  type: string;
  payload: ConfigurationInterface;
  files: any;
  callbackUpload: any;
}) {
  const user: UserInterface = yield select((state: AppStateType) => state?.auth?.user);
  try {
    if (payload?.allowPaymentLink && (!payload?.cieloClientID || !payload?.cieloSecret)) {
      yield call(notification.warning, formatMessage('configurationMessageErrorPayment'));
      return;
    }

    const marginControlByColor: any[] = payload?.marginControlByColor?.map((margin: any) => ({
      ...margin,
      initialValue: payload?.useMarginControlByColor
        ? Number(margin?.initialValue)
          ? Number(margin?.initialValue)
          : 0
        : undefined,
      finalValue: payload?.useMarginControlByColor ? Number(margin?.finalValue) : undefined,
      action: payload?.useMarginControlByColor ? margin?.action : undefined,
      color: payload?.useMarginControlByColor ? margin?.color : undefined,
      priceTableCode: payload?.useMarginControlByColor ? margin?.priceTableCode : undefined
    }));

    const hasUndefinedFieldInMargin = payload?.useMarginControlByColor
      ? marginControlByColor.some(obj => Object.values(obj).some(value => value === undefined || value === ''))
      : false;

    const blockInvalidMargin = payload?.useMarginControlByColor
      ? marginControlByColor?.some(margin => margin?.blocked)
      : false;

    if (payload?.useMarginControlByColor && blockInvalidMargin) {
      yield call(notification.warning, formatMessage('configurationMessageErrorMarginControlBlocked'));
      return;
    }

    if (payload?.useMarginControlByColor && hasUndefinedFieldInMargin) {
      yield call(notification.warning, formatMessage('configurationMessageErrorMarginControl'));
      return;
    }

    if (payload.workingHours) {
      const { enabled: enabledWorkingHours, ...rest } = payload.workingHours;
      const workingHours = [...Object.entries(rest)];
      if (enabledWorkingHours) {
        const errors: any = workingHours.reduce((array: any, item: any) => {
          const [day, dayWorkingHours] = item;
          if (dayWorkingHours?.enabled && (!dayWorkingHours?.endFirstShift || !dayWorkingHours?.endSecondShift)) {
            return [...array, day];
          } else {
            return array;
          }
        }, []);

        if (
          errors?.length > 0 ||
          workingHours.filter(([_, dayWorkingHours]: any) => dayWorkingHours?.enabled).length === 0
        ) {
          yield call(notification.warning, formatMessage('configurationMessageErrorWorkingHours'));
          return;
        }
      }
    }

    if (payload?.allowVendorTracking && payload?.allowManualVendorTracking) {
      yield call(notification.warning, formatMessage('configurationMessageErrorVendorTracking'));
      return;
    }

    if (payload?.allowChangeFreight && !payload?.freightSelected) {
      yield call(notification.warning, formatMessage('configurationMessageErrorChangeFreight'));
      return;
    }

    yield put(configurationActions.setLoadingSend(true));
    try {
      if (files?.images?.length > 0) {
        const toast: any = notification.loading(formatMessage('configurationMessageSendImagens'));
        yield call(productService.postPictures, files?.images);
        toast.close();

        yield call(notification.success, formatMessage('configurationMessageSuccessSendImagems'));
      }
    } catch (error) {
      yield put({ type: ISA_ERROR, error });
    }

    try {
      const { data } = yield call(tenantService.getTenant, user.tenantID);
      const processedTenant = data as TenantInterface;
      yield call(tenantService.putTenant, user.tenantID, {
        ...processedTenant,
        tenantInfo: {
          ...processedTenant.tenantInfo,
          pdfTemplateURL: payload?.pdfTemplateURL ?? 'modern.hbs'
        }
      });
    } catch (error) {
      yield put({ type: ISA_ERROR, error });
    }

    try {
      if (files?.shipping) {
        const toast: any = notification.loading(formatMessage('configurationMessageSendShipping'));
        yield call(configurationService.postShipping, files?.shipping);
        toast.close();

        yield call(notification.success, formatMessage('configurationMessageSuccessSendShipping'));
      }
    } catch (error) {
      yield put({ type: ISA_ERROR, error });
    }

    const deliveries = payload.deliveries;
    const reasons = payload.reasons;

    const parsedCustomizableFieldsClientRegister =
      payload?.allowClientActionFieldsCustomization && payload?.customizableControl?.clientRegister?.length > 0
        ? payload?.customizableControl?.clientRegister?.map((field: any) => ({
            name: field?.key ?? field?.name,
            type: field?.type,
            backendType: field?.backendType
          }))
        : null;

    if (parsedCustomizableFieldsClientRegister) {
      yield call(clientService.saveCustomizableClientFields, parsedCustomizableFieldsClientRegister);
    }

    delete payload._id;
    delete payload.deliveries;
    const blockReason = payload?.blockReasonSelected
      ? payload?.blockReasonSelected?.externalCode
      : payload?.blockReasonSelected ?? null;

    const { data } = yield call(configurationService.saveConfiguration, {
      ...payload,
      acceptDiscountDelivery: payload?.acceptDiscountDelivery ?? false,
      acceptBlockedSale: payload?.acceptBlockedSale ?? false,
      acceptSaleWithoutStock: payload?.acceptSaleWithoutStock ?? false,
      acceptNegativeBalance: payload?.acceptNegativeBalance ?? false,
      minimumMargin: payload?.minimumMargin ?? 0,
      maximumSalePercentage: payload?.maximumSalePercentage ?? 0,
      ...(payload.useShippingControl && payload?.deliveryControlSelected?.externalCode
        ? {
            defaultDeliveryControl: payload?.deliveryControlSelected?.externalCode
          }
        : {
            defaultDeliveryControl: null
          }),
      blockReason,
      expirationBudget: (Number(payload?.expirationBudget) || 1) * 24,
      useClientRating: payload?.useClientRating ?? false,
      blockOrderUnratedClient: payload?.blockOrderUnratedClient ?? false,
      clientRatingDeadline: payload?.clientRatingDeadline ?? 0,
      onlyProductOnKit: payload?.onlyProductOnKit ?? false,
      disableSendOrder: payload?.disableSendOrder ?? false,
      shippingByProviderDelivery: payload?.shippingByProviderDelivery ?? false,
      clientActionCharacterLimit: Number(payload?.clientActionCharacterLimit || 40),
      maxVisitTimeVendorTracking: Number(payload?.maxVisitTimeVendorTracking) ?? null,
      radiusVisit: Number(payload?.radiusVisit) ?? null,
      stayLength: Number(payload?.stayLength) ?? null,
      maxNotificationRequestClientVisitFeedback: Number(payload?.maxNotificationRequestClientVisitFeedback) ?? null,
      intervalNotificationRequestClientVisitFeedback:
        Number(payload?.intervalNotificationRequestClientVisitFeedback) ?? null,
      marginControlByColor,
      defaultBatchExpiryDate: payload?.defaultBatchExpiryDate === null ? null : Number(payload?.defaultBatchExpiryDate),
      allowClientActionFieldsCustomization: parsedCustomizableFieldsClientRegister?.length > 0
    });

    const payloadConfiguration = {
      ...data,
      reasons,
      blockReasonSelected: payload?.blockReasonSelected,
      deliveryControlSelected: data?.defaultDeliveryControl ? payload?.deliveryControlSelected : null,
      deliveries,
      clientActionCustomizableFields: payload?.customizableControl?.clientRegister
    };

    if (data) {
      yield call(AsyncStorage.setItem, ISA_CONFIGURATION, JSON.stringify(payloadConfiguration));
      yield put(configurationActions.getSuccess(payloadConfiguration));
      yield call(notification.success, formatMessage('configurationMessageSuccess'));
    }

    if (callbackUpload) {
      yield call(callbackUpload);
    }
    yield put(configurationActions.setLoadingSend(false));
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
    yield put(configurationActions.setLoadingSend(false));
  }
}

export function* reloadConfiguration() {
  yield put(configurationActions.setLoadingData(true));
  try {
    const token = yield call(AsyncStorage.getItem, ISA_TOKEN);
    if (token) {
      try {
        const [{ data }, { data: reasons }, { data: clientActionCustomizableFields }] = yield all([
          yield call(configurationService.getConfiguration),
          yield call(reasonService.getAll),
          yield call(clientService.getCustomizableClientFields)
        ]);
        let deliveryControlSelected = null;

        if (data.defaultDeliveryControl) {
          deliveryControlSelected = (yield call(orderService.getDeliveries, {
            externalCodes: data.defaultDeliveryControl
          }))?.data?.[0];
        }

        const blockReasonSelected = reasons.find(x => x.externalCode === data?.blockReason);
        if (data) {
          const payload = {
            ...data,
            reasons,
            blockReasonSelected,
            deliveryControlSelected,
            clientActionCustomizableFields: data?.allowClientActionFieldsCustomization
              ? clientActionCustomizableFields?.requiredFields?.map((field: any) => field)
              : null
          };

          yield call(AsyncStorage.setItem, ISA_CONFIGURATION, JSON.stringify(payload));
          yield put(configurationActions.getSuccess(payload));
        }
      } catch (error) {
        yield put({ type: ISA_ERROR, error });
      }
    }
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(configurationActions.setLoadingData(false));
  }
}

export default all([
  takeLatest(GET_REQUEST, getConfiguration),
  takeLatest(SAVE_REQUEST, saveConfiguration),
  takeLatest(RELOAD_REQUEST, reloadConfiguration)
]);
